import { exists } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { PublicComponent } from 'base/library_service';
import { observable } from 'mobx';
import { anElementInstance, Page, Project, Section } from '../model';
import {
  allProjectPages,
  findParentSpace,
  pageAndAllSubpages,
  unrefPresentSections,
} from '../model_helpers';
import { SelectionFactory } from '../selection/factory';
import { selectionLocale } from '../selection/store';
import { SectionToComponentCategoryAi } from './section_to_component_category';
import { TaskContext } from './task_log';

export type ChooseComponentOptions = {
  replaceExisting?: boolean;
};

export class PageComponentsAi {
  constructor(
    private readonly sectionToComponentCategory: SectionToComponentCategoryAi,
    private readonly library: LibraryCache,
    private readonly project: Project,
    private readonly selectionFactory: SelectionFactory,
  ) {}

  private readonly pending = observable.set<Page>();

  isPending(page: Page) {
    return this.pending.has(page);
  }

  async chooseComponents(
    page: Page,
    sections: readonly Section[],
    ctx: TaskContext,
  ) {
    const pageSections = unrefPresentSections(page.sections, this.project);
    if (!sections.every((s) => pageSections.includes(s))) {
      throw new Error('sections must be from the same page');
    }
    if (this.pending.has(page)) {
      throw new Error('Already choosing components');
    }
    this.pending.add(page);

    try {
      const locale = selectionLocale(
        this.selectionFactory.forPage(page),
        this.project,
      );
      const categoryBySection = await Promise.all(
        sections.map((section) =>
          this.sectionToComponentCategory.getBlocksCategoryForSection(
            locale,
            page.state,
            section.state,
          ),
        ),
      );
      const uniqueCategories = [...new Set(categoryBySection)]
        .filter((x) => !!x)
        .filter(exists);
      const resultsByCategory = new Map(
        await Promise.all(
          uniqueCategories.map(async (name) => {
            const res = await this.library.findPublicComponents({
              filterBlocksCategoryAi: [name],
            });
            return [name, res.components] as const;
          }),
        ),
      );

      const consistent = findConsistentChoices(page, this.project);

      sections.forEach((section, i) => {
        const category = categoryBySection[i];
        const searchResults = resultsByCategory.get(category || '');

        const above = sections[i - 1]?.element.current;
        const old = section.element.current;
        const newSlug = chooseResult(
          searchResults || [],
          consistent,
          above ? above.source.slug : undefined,
          old ? old.source.slug : undefined,
        );
        if (!newSlug) {
          ctx.buffer.run(() => {
            section.element.replace(undefined);
            section.isComponentOutOfSync = false;
          });
          return;
        }
        if (old && newSlug === old.source.slug) {
          // keep the existing text to save some work
          return;
        }
        ctx.buffer.run(() => {
          section.element.replace(
            anElementInstance({ source: { slug: newSlug } }),
          );
          section.isComponentOutOfSync = false;
        });
      });
    } finally {
      this.pending.delete(page);
    }
  }
}

export function chooseResult(
  searchResults: PublicComponent[],
  consistent: ConsistentChoices,
  componentSlugAbove: string | undefined,
  oldComponentSlug: string | undefined,
  random: () => number = Math.random,
): string | undefined {
  // Evaluate consistent choices first.
  // The consistent navbar might not be included in the search results, as there are
  // a lot of navbars.
  if (
    mostly(searchResults, (r) => r.componentSlug.startsWith('navbar')) &&
    consistent.navbar
  ) {
    return consistent.navbar;
  }
  if (
    mostly(searchResults, (r) => r.componentSlug.startsWith('footer')) &&
    consistent.footer
  ) {
    return consistent.footer;
  }

  // Otherwise, choose the best result:
  const nResults = searchResults.length;
  const scored = searchResults.map((r, i) => {
    const sameAsAbove = r.componentSlug === componentSlugAbove ? 1 : 0;
    const sameAsOld = r.componentSlug === oldComponentSlug ? 1 : 0;
    const score =
      3 * random() +
      // Slight preference for the top result:
      (nResults - i) / nResults +
      -10 * sameAsAbove +
      -20 * sameAsOld;
    return { slug: r.componentSlug, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.slug;
}

/* Choices that should be consistent across all pages.  Case to component slug */
type ConsistentChoices = {
  navbar: string | undefined;
  footer: string | undefined;
};

function findConsistentChoices(
  page: Page,
  project: Project,
): ConsistentChoices {
  const parent = findParentSpace(project, page) || project.homePage;
  const nearby = findConsistentChoicesIn(pageAndAllSubpages(parent), project);
  if (nearby.navbar && nearby.footer) {
    return nearby;
  }
  const all = findConsistentChoicesIn(allProjectPages(project), project);
  return {
    navbar: nearby.navbar || all.navbar,
    footer: nearby.footer || all.footer,
  };
}

function findConsistentChoicesIn(
  pages: readonly Page[],
  project: Project,
): ConsistentChoices {
  const components = pages
    .flatMap((p) => unrefPresentSections(p.sections, project))
    .map((section) => section.element?.current?.source)
    .filter(exists)
    .map((source) => source.slug);
  const navbar = mostConsistent(
    components.filter((c) => c.startsWith('navbar')),
  );
  const footer = mostConsistent(
    components.filter((c) => c.startsWith('footer')),
  );
  return { navbar, footer };
}

/** If at least half of the items in xs match the predicate, return true */
function mostly<T>(xs: readonly T[], predicate: (x: T) => boolean): boolean {
  const n = xs.length;
  if (n === 0) return false;
  const threshold = Math.ceil(n / 2);
  let matching = 0;
  for (const x of xs) {
    if (predicate(x)) {
      matching++;
      if (matching >= threshold) return true;
    }
  }
  return false;
}

function mostConsistent<T extends string | number | undefined>(
  xs: readonly T[],
): T | undefined {
  const counts = new Map<T, number>();
  for (const x of xs) {
    counts.set(x, (counts.get(x) || 0) + 1);
  }
  let best: T | undefined;
  let bestCount = 0;
  for (const [x, count] of counts) {
    if (count > bestCount) {
      best = x;
      bestCount = count;
    }
  }
  return best;
}
